import {
  ApiTokenListResponse,
  LeadDTO,
  LeadDTOItem,
  LeadPipelineItem,
  LeadPostDTO,
  LeadReportDTO,
  LeadStatusDTO,
  OpenLeadReportDTO,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

import { createSortParam } from "../createSortParam";
import { trimFields } from "./features/trimFiels";

export type LeadApiType = ReturnType<typeof leadApi>;

const leadApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;
  const fieldsToTrim = [
    "firstName",
    "lastName",
    "phoneNumber",
    "email",
    "source",
  ];

  return {
    findById(id: number) {
      return axiosInstance.get<LeadDTO>(`${clubPrefix}lead/${id}`);
    },
    create(newResource: LeadPostDTO) {
      return axiosInstance.post<LeadDTOItem>(
        `${clubPrefix}lead`,
        trimFields(newResource, fieldsToTrim),
      );
    },
    update(id: number, patchedFields: Partial<LeadPostDTO>) {
      return axiosInstance.patch(
        `${clubPrefix}lead/${id}`,
        trimFields(patchedFields, fieldsToTrim),
      );
    },
    find({
      statusIds,
      source,
      dateFrom,
      dateTo,
    }: {
      dateFrom?: string;
      dateTo?: string;
      source?: number[];
      statusIds?: number[];
    }) {
      const params = {} as any;
      if (statusIds) {
        params["leadStatus.id"] = statusIds;
      }
      if (source) {
        params["source.id"] = source;
      }
      if (dateFrom) {
        params.dateFrom = dateFrom;
      }
      if (dateTo) {
        params.dateTo = dateTo;
      }
      return axiosInstance.get<LeadDTO[]>(`${clubPrefix}lead`, { params });
    },
    list(params: LeadListFilter) {
      return axiosInstance.get<ApiTokenListResponse<LeadPipelineItem>>(
        `${clubPrefix}lead-list`,
        {
          params,
        },
      );
    },
    listCount(params: LeadListFilter) {
      return axiosInstance.get<{ count: number }>(
        `${clubPrefix}lead-list/count`,
        {
          params,
        },
      );
    },
    listCsv(params: LeadListFilter) {
      return axiosInstance.get<string>(`${clubPrefix}lead-list/csv`, {
        params,
        headers: {
          "Content-Disposition": "attachment; filename=leads.csv",
        },
      });
    },

    findStatus() {
      return axiosInstance.get<LeadStatusDTO[]>(`${clubPrefix}lead/status`);
    },
    changeLeadStatusColumn(leadId: number, columnId: number) {
      return axiosInstance.patch<{
        leadId: number;
        newStatusId: number;
      }>(`${clubPrefix}lead`, {
        leadId,
        newStatusId: columnId,
      });
    },
    createStatusColumn(name: string) {
      return axiosInstance.post(`${clubPrefix}lead/status`, {
        name,
      });
    },
    editStatusColumnName(columnId: number, name: string) {
      return axiosInstance.patch(`${clubPrefix}lead/status/${columnId}/name`, {
        name,
      });
    },

    editStatusColumnOrder(columnId: number, statusOrder: number) {
      return axiosInstance.patch(`${clubPrefix}lead/status/${columnId}/order`, {
        statusOrder,
      });
    },
    deleteStatusColumn(columnId: number) {
      return axiosInstance.delete(`${clubPrefix}lead/status/${columnId}`);
    },
    report({
      startDate,
      endDate,
      presetLeadStatus,
      leadSourceList,
      page = 0,
      limit = 10,
      sort,
    }: {
      startDate: string;
      endDate: string;
      presetLeadStatus: string;
      leadSourceList?: number[];
      page?: number;
      limit?: number;
      sort: any;
    }) {
      const config = {
        params: {
          dateFrom: startDate,
          dateTo: endDate,
          presetLeadStatus,
          leadSourceList:
            leadSourceList && leadSourceList.length > 0
              ? leadSourceList
              : undefined,
          page,
          size: limit,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get<LeadReportDTO>(
        `${clubPrefix}lead/report/preset-status`,
        config,
      );
    },
    reportCsv(filters: {
      dateFrom: string;
      dateTo: string;
      presetLeadStatus: string;
      leadSourceList?: number[];
      page?: number;
      limit?: number;
      sort?: any;
    }) {
      return axiosInstance.get(`${clubPrefix}lead/report/preset-status/csv`, {
        params: filters,
      });
    },
    openReport({
      leadStatusList,
      leadSourceList,
      page = 0,
      limit = 10,
      sort,
    }: {
      leadStatusList?: string[];
      leadSourceList?: number[];
      page?: number;
      limit?: number;
      sort: any;
    }) {
      const config = {
        params: {
          leadStatusList:
            leadStatusList && leadStatusList.length > 0
              ? leadStatusList
              : undefined,
          leadSourceList:
            leadSourceList && leadSourceList.length > 0
              ? leadSourceList
              : undefined,
          page,
          size: limit,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }

      return axiosInstance.get<OpenLeadReportDTO>(
        `${clubPrefix}lead/report/open-status`,
        config,
      );
    },
    openReportCsv(filters: {
      leadStatusList: string[];
      leadSourceList?: number[];
      page?: number;
      limit?: number;
      sort?: any;
    }) {
      return axiosInstance.get(`${clubPrefix}lead/report/open-status/csv`, {
        params: filters,
      });
    },
    delete(leadId: number) {
      return axiosInstance.delete(`${clubPrefix}lead/${leadId}`);
    },
    bulkStatusUpdate(newLeadStatusId: number, leadIds: number[]) {
      return axiosInstance.put(
        `${clubPrefix}lead-list/bulk-status-update/no-filter`,
        {
          newLeadStatusId,
          leadIds,
        },
      );
    },
    bulkStatusUpdateFilter(params: {
      newLeadStatusId: number;
      leadIdsToExclude?: number[];
      createdFrom?: string;
      createdTo?: string;
      smsCommunication?: boolean;
      emailCommunication?: boolean;
      leadStatusIdList?: number[];
      leadSourceIdList?: number[];
    }) {
      return axiosInstance.put(
        `${clubPrefix}lead-list/bulk-status-update/filter`,
        params,
      );
    },
  };
};

export default leadApi;
export type LeadListFilter = {
  limit?: number;
  createdFrom?: string;
  createdTo?: string;
  smsCommunication?: boolean;
  emailCommunication?: boolean;
  leadStatusId?: number[];
  leadSourceId?: number[];
  includeNotCompleteTasks?: boolean;
};
